import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import {
  ReportSection,
  ReportItem,
  ReportMode,
  DragItem,
  Photo,
} from '@/types';
import { sortByOrder } from '@/utils/helpers';
import { severityKey } from '@/ui/severity';
import { useAutoSaveContext } from '@/hooks/useReportAutoSave';
import {
  IconChevDown,
  IconGrip,
  IconPlus,
  IconTrash,
} from './_icons';
import { ItemTypes } from './_types';
import { ItemCard } from './ItemCard';

interface SectionCardProps {
  section: ReportSection;
  index: number;
  mode: ReportMode;
  propertyZip?: string;
  onUpdate: (updates: Partial<ReportSection>) => void;
  onDelete: () => void;
  onAddItem: () => void;
  onUpdateItem: (itemId: string, updates: Partial<ReportItem>) => Promise<void>;
  onUpdateItemPhotos: (itemId: string, photos: Photo[]) => void;
  onDeleteItem: (itemId: string) => void;
  onDuplicateItem: (item: ReportItem) => void;
  onMoveItem: (dragIndex: number, hoverIndex: number) => void;
  onMove: (dragIndex: number, hoverIndex: number) => void;
}

const GripIcon: React.FC = () => <IconGrip size={14} />;

export const SectionCard: React.FC<SectionCardProps> = ({
  section,
  index,
  mode,
  propertyZip,
  onUpdate,
  onDelete,
  onAddItem,
  onUpdateItem,
  onUpdateItemPhotos,
  onDeleteItem,
  onDuplicateItem,
  onMoveItem,
  onMove,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [title, setTitle] = useState(section.title);
  const autoSave = useAutoSaveContext();
  const scheduleSave = autoSave?.scheduleSave;
  // Ref the parent `onUpdate` so the auto-save effect doesn't list it as a
  // dependency — the inline arrow in the parent is recreated every render,
  // which would otherwise re-fire the effect on every save-state change.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => { onUpdateRef.current = onUpdate; });
  // Tracks the last `section.title` we synced from. Lets the prop→state
  // sync below skip when the user has typed since (otherwise an in-flight
  // save returning the older value would clobber unsaved keystrokes).
  const lastExternalTitleRef = useRef(section.title);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const dragHandleRef = React.useRef<HTMLButtonElement>(null);

  const [{ isDragging }, drag, dragPreview] = useDrag({
    type: ItemTypes.SECTION,
    item: { id: section.id, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [, drop] = useDrop({
    accept: ItemTypes.SECTION,
    hover: (item: DragItem) => {
      if (!containerRef.current) return;
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      onMove(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  drag(dragHandleRef);
  drop(dragPreview(containerRef));

  useEffect(() => {
    if (title === lastExternalTitleRef.current) {
      setTitle(section.title);
    }
    lastExternalTitleRef.current = section.title;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.title]);

  // Debounced auto-save: schedule a PATCH whenever the typed value diverges
  // from the server value. Empty titles are never saved — the blur handler
  // reverts them.
  useEffect(() => {
    if (!scheduleSave) return;
    const trimmed = title.trim();
    if (!trimmed || trimmed === section.title) return;
    scheduleSave(`section:${section.id}:title`, async () => {
      await onUpdateRef.current({ title: trimmed });
    });
  }, [title, section.id, section.title, scheduleSave]);

  const handleBlur = () => {
    if (!title.trim()) setTitle(section.title);
  };

  const counts = useMemo(() => {
    let safety = 0, recommend = 0;
    for (const item of section.items) {
      const k = severityKey(item.severity);
      if (k === 'safety_concern') safety++;
      else if (k === 'recommendation') recommend++;
    }
    return { safety, recommend };
  }, [section.items]);

  const classNames = [
    're-sec',
    isOpen ? 're-sec--open' : '',
    isDragging ? 're-sec--dragging' : '',
  ].filter(Boolean).join(' ');

  return (
    <div ref={containerRef} className={classNames}>
      <div className="re-sec-head">
        <button
          ref={dragHandleRef}
          className="re-sec-grip"
          aria-label="Drag section"
          type="button"
        >
          <GripIcon />
        </button>
        <div className="re-sec-idx">{String(index + 1).padStart(2, '0')}</div>
        <input
          className="re-sec-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.currentTarget.blur();
            } else if (e.key === 'Escape') {
              setTitle(section.title);
              e.currentTarget.blur();
            }
          }}
          aria-label="Section title"
        />
        <span className="re-sec-count">{section.items.length}</span>
        <div className="re-sec-sev">
          {counts.safety > 0 && (
            <span className="f-pill f-pill--safety" title="Safety concerns">{counts.safety}</span>
          )}
          {counts.recommend > 0 && (
            <span className="f-pill f-pill--recommend" title="Recommendations">{counts.recommend}</span>
          )}
        </div>
        <div className="re-sec-spacer" />
        <div className="re-sec-actions">
          <button className="re-add" onClick={onAddItem} type="button">
            <IconPlus size={12} />
            Add item
          </button>
          <button
            className="re-icon-btn-sm"
            onClick={onDelete}
            title="Delete section"
            aria-label="Delete section"
            type="button"
          >
            <IconTrash size={14} />
          </button>
          <button
            className="re-icon-btn-sm"
            onClick={() => setIsOpen(v => !v)}
            aria-label={isOpen ? 'Collapse section' : 'Expand section'}
            aria-expanded={isOpen}
            type="button"
          >
            <span className="re-sec-caret">
              <IconChevDown size={14} />
            </span>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="re-items">
          {sortByOrder(section.items).map((item, itemIndex) => (
            <ItemCard
              key={item.id}
              item={item}
              index={itemIndex}
              mode={mode}
              propertyZip={propertyZip}
              sectionName={section.title}
              onUpdate={(updates) => onUpdateItem(item.id, updates)}
              onPhotosUpdate={(photos) => onUpdateItemPhotos(item.id, photos)}
              onDelete={() => onDeleteItem(item.id)}
              onDuplicate={() => onDuplicateItem(item)}
              onMoveItem={onMoveItem}
            />
          ))}
          {section.items.length === 0 && (
            <div className="re-sec-empty">
              <p>No deficiencies noted.</p>
              <button className="re-add" onClick={onAddItem} type="button">
                <IconPlus size={12} />
                Add item
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
